import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["statusIndicator"]
  static values = {
    url: String,
    interval: { type: Number, default: 30 },
    enabled: { type: Boolean, default: true }
  }

  connect() {
    console.log('Auto-refresh controller connected', {
      url: this.urlValue,
      interval: this.intervalValue,
      enabled: this.enabledValue
    })

    this.countdownValue = this.intervalValue

    // Check if visual elements exist
    const hasVisualElements = document.getElementById('auto-refresh-status') &&
                              document.getElementById('connection-dot') &&
                              document.getElementById('status-text')

    if (hasVisualElements) {
      console.log('Visual indicator elements found')
      this.updateStatusIndicator('connected', 'Connected')
    } else {
      console.log('Visual indicator elements not found - running in basic mode')
    }

    if (this.enabledValue) {
      this.startRefresh()
    }

    // Add manual refresh button functionality
    this.setupRefreshButton()

    // Pause refresh when page is not visible
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this))
  }

  disconnect() {
    this.stopRefresh()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this))
  }

  startRefresh() {
    this.stopRefresh() // Clear any existing intervals

    if (this.enabledValue && this.intervalValue > 0) {
      this.countdownValue = this.intervalValue
      this.updateStatusIndicator('active', 'Auto-refresh active')

      // Start countdown timer (updates every second)
      this.countdownInterval = setInterval(() => {
        this.countdownValue--
        this.updateCountdown()

        if (this.countdownValue <= 0) {
          this.countdownValue = this.intervalValue
        }
      }, 1000)

      // Start refresh timer
      this.refreshInterval = setInterval(() => {
        this.refresh()
      }, this.intervalValue * 1000)

      console.log(`Auto-refresh started: ${this.intervalValue}s interval`)
      console.log(`Next refresh in ${this.intervalValue} seconds at:`, new Date(Date.now() + this.intervalValue * 1000).toLocaleTimeString())
    }
  }

  stopRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
    this.updateStatusIndicator('stopped', 'Auto-refresh stopped')
  }

  async refresh() {
    if (!this.urlValue || document.hidden) {
      console.log('Auto-refresh skipped:', { noUrl: !this.urlValue, hidden: document.hidden })
      return
    }

    console.log(`🔄 Auto-refresh executing at ${new Date().toLocaleTimeString()} - fetching:`, this.urlValue)

    // Show refreshing state
    this.updateStatusIndicator('refreshing', 'Refreshing...')

    try {
      const response = await fetch(this.urlValue, {
        method: 'GET',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      console.log('Auto-refresh response:', { status: response.status, ok: response.ok })

      if (response.ok) {
        const html = await response.text()
        console.log('Auto-refresh HTML received:', html.length, 'characters')
        if (html.trim()) {
          Turbo.renderStreamMessage(html)
          console.log('Turbo stream rendered successfully')
        }

        // Return to active state
        this.updateStatusIndicator('active', 'Auto-refresh active')
      } else {
        this.updateStatusIndicator('error', 'Refresh failed')
        setTimeout(() => {
          this.updateStatusIndicator('active', 'Auto-refresh active')
        }, 2000)
      }
    } catch (error) {
      console.error('Auto-refresh failed:', error)
      this.updateStatusIndicator('error', 'Connection error')
      setTimeout(() => {
        this.updateStatusIndicator('active', 'Auto-refresh active')
      }, 2000)
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stopRefresh()
    } else {
      this.startRefresh()
      // Immediately refresh when page becomes visible again
      setTimeout(() => this.refresh(), 100)
    }
  }

  setupRefreshButton() {
    const refreshButton = document.getElementById('refresh-jobs')
    if (refreshButton) {
      refreshButton.addEventListener('click', () => {
        this.refresh()

        // Visual feedback (preserve dark mode styling)
        const originalHTML = refreshButton.innerHTML
        refreshButton.innerHTML = `
          <svg class="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refreshing...
        `
        refreshButton.disabled = true
        refreshButton.classList.add('opacity-75')

        setTimeout(() => {
          refreshButton.innerHTML = originalHTML
          refreshButton.disabled = false
          refreshButton.classList.remove('opacity-75')
        }, 1000)
      })
    }
  }

  // Action to toggle auto-refresh (dark mode styling)
  toggle() {
    this.enabledValue = !this.enabledValue

    const toggleButton = document.getElementById('auto-refresh-toggle')

    if (this.enabledValue) {
      this.startRefresh()
      if (toggleButton) {
        try {
          toggleButton.innerHTML = `
            <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Auto (30s)
          `
          toggleButton.className = 'inline-flex items-center px-4 py-2 border border-emerald-700/50 shadow-sm text-sm font-medium rounded-lg text-emerald-300 bg-admin-dark hover:bg-emerald-900/20 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors'
        } catch (error) {
          console.error('Error updating toggle button:', error)
        }
      }
    } else {
      this.stopRefresh()
      if (toggleButton) {
        try {
          toggleButton.innerHTML = `
            <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Manual
          `
          toggleButton.className = 'inline-flex items-center px-4 py-2 border border-emerald-900/30 shadow-sm text-sm font-medium rounded-lg text-gray-300 bg-admin-dark hover:bg-emerald-900/20 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors'
        } catch (error) {
          console.error('Error updating toggle button:', error)
        }
      }
    }
  }

  // Action to manually refresh
  manualRefresh() {
    this.refresh()
  }

  // Visual status indicator management (dark mode)
  updateStatusIndicator(status, message) {
    const statusIndicator = document.getElementById('auto-refresh-status')
    const connectionDot = document.getElementById('connection-dot')
    const statusText = document.getElementById('status-text')

    if (!statusIndicator || !connectionDot || !statusText) {
      console.log('Status indicator elements not found, skipping visual update')
      return
    }

    // Update dot color and status based on state (dark mode styling)
    switch (status) {
      case 'connected':
        connectionDot.className = 'w-2 h-2 rounded-full bg-blue-400 mr-2'
        statusIndicator.className = 'inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-admin-dark text-blue-300 border border-blue-700/50'
        break
      case 'active':
        connectionDot.className = 'w-2 h-2 rounded-full bg-emerald-400 mr-2 animate-pulse'
        statusIndicator.className = 'inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-admin-dark text-emerald-300 border border-emerald-700/50'
        break
      case 'refreshing':
        connectionDot.className = 'w-2 h-2 rounded-full bg-yellow-400 mr-2 animate-spin'
        statusIndicator.className = 'inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-admin-dark text-yellow-300 border border-yellow-700/50'
        break
      case 'stopped':
        connectionDot.className = 'w-2 h-2 rounded-full bg-gray-500 mr-2'
        statusIndicator.className = 'inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-admin-dark text-gray-400 border border-gray-700/50'
        break
      case 'error':
        connectionDot.className = 'w-2 h-2 rounded-full bg-red-400 mr-2'
        statusIndicator.className = 'inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-admin-dark text-red-300 border border-red-700/50'
        break
    }

    statusText.textContent = message
  }

  updateCountdown() {
    const countdownText = document.getElementById('countdown-text')
    if (!countdownText) {
      console.log('Countdown text element not found, skipping countdown update')
      return
    }

    if (this.countdownValue > 0) {
      countdownText.textContent = `• ${this.countdownValue}s`
    } else {
      countdownText.textContent = '• refreshing...'
    }
  }
}